import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './TestimonialCarousel.css';

const QuoteIcon = () => (
  <svg className="testimonial-quote-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21zm12 0c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" fill="currentColor"/>
  </svg>
);

const TestimonialCarousel = ({ testimonials }) => {
  const [perPage, setPerPage] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  // Show 1 card per view on mobile, 3 on larger screens.
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const update = () => setPerPage(mql.matches ? 1 : 3);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - perPage);

  // Keep the active index in range when the viewport (perPage) changes.
  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, Math.max(0, testimonials.length - perPage)));
  }, [perPage, testimonials.length]);

  const hasControls = testimonials.length > perPage;
  const pageCount = maxIndex + 1;

  const prev = () => setCurrentIndex((i) => (i - 1 + pageCount) % pageCount);
  const next = () => setCurrentIndex((i) => (i + 1) % pageCount);

  // Touch swipe navigation
  const minSwipeDistance = 50;
  const onTouchStart = (e) => {
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };
  const onTouchMove = (e) => { touchEndRef.current = e.targetTouches[0].clientX; };
  const onTouchEnd = () => {
    if (touchStartRef.current === null || touchEndRef.current === null) return;
    const distance = touchStartRef.current - touchEndRef.current;
    if (distance > minSwipeDistance) next();
    else if (distance < -minSwipeDistance) prev();
  };

  return (
    <div className="testimonial-carousel">
      {hasControls && (
        <button
          className="testimonial-nav testimonial-nav-prev"
          onClick={prev}
          aria-label="Previous testimonials"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      <div className="testimonial-viewport">
        <div
          className="testimonial-track"
          style={{ transform: `translateX(-${currentIndex * (100 / perPage)}%)` }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {testimonials.map((t) => (
            <div key={t.name} className="testimonial-slide">
              <div className="testimonial-card">
                <QuoteIcon />
                <blockquote className="testimonial-quote">{t.quote}</blockquote>
                <div className="testimonial-author">
                  {t.logo && (
                    <img
                      src={t.logo}
                      alt={`${t.title} logo`}
                      className="testimonial-logo"
                      loading="lazy"
                    />
                  )}
                  <div className="testimonial-author-info">
                    <span className="testimonial-author-name">{t.name}</span>
                    <span className="testimonial-author-title">{t.title}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {hasControls && (
        <button
          className="testimonial-nav testimonial-nav-next"
          onClick={next}
          aria-label="Next testimonials"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {hasControls && (
        <div className="testimonial-dots">
          {Array.from({ length: maxIndex + 1 }, (_, index) => (
            <button
              key={index}
              className={`testimonial-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to testimonial group ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

TestimonialCarousel.propTypes = {
  testimonials: PropTypes.arrayOf(PropTypes.shape({
    quote: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    logo: PropTypes.string,
  })).isRequired,
};

export default TestimonialCarousel;
